import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  RadioGroup, FormControlLabel, Radio, Divider,
} from '@mui/material';
import DiffViewer from '../common/DiffViewer';
import { htmlToText } from '../../utils/htmlText';

/**
 * Field-level merge UI for a 409 conflict (#161 — the gap #145/#159 left open:
 * those shipped a bare "Reload latest" / "Keep my edits" choice, an all-or-
 * nothing pick between the two whole versions). This adds a third option:
 * merge, picking MINE or THEIRS independently per field (title, description),
 * with a diff so the user can see exactly what changed before choosing.
 *
 * Attributes are NOT offered a per-field choice here — they stay whatever the
 * caller's current form state holds (the same "keep mine" behaviour Overwrite
 * already had). Attribute-level merge (per-key mine/theirs) is a reasonable
 * follow-up but the attribute set can differ in SHAPE (added/removed keys, not
 * just changed values) between the two versions, which needs its own row-level
 * UI — out of scope for this pass; title/description are the fields the 409
 * banner and the manual-save form actually edit inline.
 */
export default function ConflictMergeDialog({ open, onClose, mine, theirs, onMerge }) {
  const [titleChoice, setTitleChoice] = useState('mine');
  const [descriptionChoice, setDescriptionChoice] = useState('mine');

  // Reset the picks each time a FRESH conflict opens the dialog — a stale pick
  // from a previously-resolved conflict must not silently carry over.
  useEffect(() => {
    if (open) {
      setTitleChoice('mine');
      setDescriptionChoice('mine');
    }
  }, [open]);

  const mineTitle = mine?.title ?? '';
  const theirsTitle = theirs?.title ?? '';
  const mineDescription = mine?.description ?? '';
  const theirsDescription = theirs?.description ?? '';
  const titleDiffers = htmlToText(mineTitle) !== htmlToText(theirsTitle);
  const descriptionDiffers = htmlToText(mineDescription) !== htmlToText(theirsDescription);

  const handleMerge = () => {
    onMerge({
      title: titleChoice === 'mine' ? mineTitle : theirsTitle,
      description: descriptionChoice === 'mine' ? mineDescription : theirsDescription,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      data-testid="conflict-merge-dialog"
    >
      <DialogTitle>Merge conflicting changes</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Someone else saved this item since you loaded it. Pick which version wins for
          each field, then merge — the result saves over the latest server copy.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Title</Typography>
          <RadioGroup
            row
            value={titleChoice}
            onChange={(e) => setTitleChoice(e.target.value)}
            data-testid="merge-title-choice"
          >
            <FormControlLabel value="mine" control={<Radio size="small" />}
              data-testid="merge-title-mine"
              label={`Mine: "${mineTitle}"`} />
            <FormControlLabel value="theirs" control={<Radio size="small" />}
              data-testid="merge-title-theirs"
              label={`Theirs: "${theirsTitle}"`} disabled={!titleDiffers} />
          </RadioGroup>
          {!titleDiffers && (
            <Typography variant="caption" color="text.secondary">Title is unchanged between the two versions.</Typography>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Description</Typography>
          <RadioGroup
            row
            value={descriptionChoice}
            onChange={(e) => setDescriptionChoice(e.target.value)}
            data-testid="merge-description-choice"
          >
            <FormControlLabel value="mine" control={<Radio size="small" />}
              data-testid="merge-description-mine"
              label="Mine" />
            <FormControlLabel value="theirs" control={<Radio size="small" />}
              data-testid="merge-description-theirs"
              label="Theirs" disabled={!descriptionDiffers} />
          </RadioGroup>
          {descriptionDiffers ? (
            <DiffViewer original={htmlToText(theirsDescription)} modified={htmlToText(mineDescription)} />
          ) : (
            <Typography variant="caption" color="text.secondary">Description is unchanged between the two versions.</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="conflict-merge-cancel">Cancel</Button>
        <Button onClick={handleMerge} variant="contained" color="primary" data-testid="conflict-merge-confirm">
          Merge &amp; save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ConflictMergeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mine: PropTypes.shape({ title: PropTypes.string, description: PropTypes.string }),
  theirs: PropTypes.shape({ title: PropTypes.string, description: PropTypes.string }),
  onMerge: PropTypes.func.isRequired,
};
